import { notifyError } from "@/lib/notify-error";
import { useState, useCallback, useMemo, useRef, Suspense, lazy, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { FunctionReference } from "convex/server";
import {
  RotateCcw,
  AlertCircle,
  Loader2,
  FileText,
  Plus,
  Save,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { PageHeader, PageHeaderSkeleton } from "@/components/layout";
import { StatusBadge } from "@/components/status-badge";
import type { EditableDataGridHandle } from "@/components/editable-data-grid";
import { ProcoreTemplateGrid } from "@/components/submittal/procore-template-grid";
import { ProcessingSpinner } from "@/components/processing-spinner";
import type { TrackerType } from "@/components/tracker-view-modal";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import {
  PdfModalPip,
  type PdfModalPipMode,
  type PdfModalPipSourceRef,
} from "@/components/pdf-modal-pip";
import { downloadFile, toCSV } from "@/lib/csv";
import { downloadUrlAsFile } from "@/lib/document-download";
import { useRecordExportDownloaded } from "@/features/onboarding";
import { withExportExtension } from "@/lib/export-filenames";
import { ExportButton } from "@/features/exports/export-button";
import { exportTarget } from "@/features/exports/export-names";
import { useExportFilenames } from "@/features/exports/use-export-filenames";
import { SubmittalExportDialog } from "@/features/spec-pilot/submittal/submittal-export-dialog";
import { findExactColumnIndex } from "@/lib/table-utils";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { IconButton } from "@/components/ui/icon-button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { uploadTableRowsPayload } from "@/lib/table-rows-upload";
import { useRouteParam } from "@/lib/use-route-param";
import {
  GLOBAL_PROCESSING_COMPLETED,
  GLOBAL_PROCESSING_FAILED,
  GLOBAL_PROCESSING_PENDING,
  GLOBAL_PROCESSING_PROCESSING,
} from "@/lib/processing-states";
import { ActionLogProvider, useRecordActionLog } from "@/hooks/use-action-log-session";

import {
  useSubmittalData,
  type VersionType,
} from "@/features/spec-pilot/submittal/use-submittal-data";
import { useSubmittalColumnMap } from "@/features/spec-pilot/submittal/submittal-columns";
import { useSubmittalBulkActions } from "@/features/spec-pilot/submittal/use-submittal-bulk-actions";
import {
  useSubmittalCellRenderer,
  type TemplateConfigColumn,
} from "@/features/spec-pilot/submittal/use-submittal-cell-renderer";
import { getSubmittalStateGate } from "@/features/spec-pilot/submittal/submittal-state-gate";
import { SubmittalSetupPanel } from "@/features/spec-pilot/submittal/submittal-setup-panel";
import { SubmittalSelectionToolbar } from "@/features/spec-pilot/submittal/submittal-selection-toolbar";
import { SubmittalTrackerStrip } from "@/features/spec-pilot/submittal/submittal-tracker-strip";
import { AddRowsDialog } from "@/features/spec-pilot/submittal/add-rows-dialog";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

export const Route = createFileRoute("/specPilot/$jobId_/submittal")({
  component: SubmittalViewerPage,
});

type TrackerCountItem = { trackerType: "warranty" | "lead_time" | "attic_stock" | "custom"; customTrackerName?: string };

function getDisplayHeaders(displayData?: { headers: string[] } | null) {
  return displayData?.headers ?? [];
}

function getTemplateLabel(templateName?: string) {
  return templateName ?? "Submittal Template";
}

type TemplateSnapshotColumn = {
  key?: string;
  header?: string;
  type?: string;
  defaultValue?: string;
};

function isTemplateSnapshot(value: unknown): value is { source?: string; columns?: TemplateSnapshotColumn[] } {
  return typeof value === "object" && value !== null && "columns" in value;
}

function getTemplateConfigColumns(
  snapshot: unknown,
  headers?: string[]
): TemplateConfigColumn[] {
  if (!isTemplateSnapshot(snapshot) || !Array.isArray(snapshot.columns) || !headers) {
    return [];
  }

  return snapshot.columns.flatMap((column) => {
    if (!["team_member", "project_item", "duration", "date"].includes(column.type ?? "")) {
      return [];
    }
    const header = column.header ?? "";
    const key = column.key ?? header;
    const columnIndex = findExactColumnIndex(headers, [header]);
    if (!key || !header || columnIndex < 0) return [];
    return [{
      key,
      header,
      type: column.type as TemplateConfigColumn["type"],
      columnIndex,
      defaultValue: column.defaultValue,
    }];
  });
}

function getManagerSelected(
  templateConfigColumns: TemplateConfigColumn[],
  configValues: Record<string, string>,
  submittalManager: string | null
) {
  const teamMemberColumns = templateConfigColumns.filter((column) => column.type === "team_member");
  if (teamMemberColumns.length === 0) return submittalManager !== null;
  return teamMemberColumns.some((column) => Boolean(configValues[column.key]));
}

function getProjectCustomItemsArgs(projectId?: Id<"projects">) {
  return projectId ? { projectId } : "skip";
}

type SpecPilotPdfExportApi = {
  getLatest: FunctionReference<
    "query",
    "public",
    { submittalId: Id<"specPilotSubmittals"> },
    {
      artifactId: Id<"exportArtifacts">;
      status: string;
      error?: string;
      generatedAt?: number;
      operationId?: Id<"operations">;
      options: unknown;
    } | null
  >;
  getDownloadUrl: FunctionReference<
    "query",
    "public",
    { submittalId: Id<"specPilotSubmittals"> },
    string | null
  >;
  generate: FunctionReference<
    "mutation",
    "public",
    { submittalId: Id<"specPilotSubmittals">; version?: VersionType },
    { artifactId: Id<"exportArtifacts">; operationId: Id<"operations">; status: "pending" }
  >;
};

const specPilotPdfExportApi = (api as unknown as { specPilot: { pdfExport: SpecPilotPdfExportApi } })
  .specPilot.pdfExport;

function useSpecPilotPdfExport({
  enabled,
  submittalId,
  version,
  hasData,
  downloadName,
}: {
  enabled: boolean;
  submittalId: Id<"specPilotSubmittals">;
  version: VersionType;
  hasData: boolean;
  /** Saved or default name, without extension. */
  downloadName: string;
}) {
  const generatePdfExport = useMutation(specPilotPdfExportApi.generate);
  const recordExportDownloaded = useRecordExportDownloaded();
  const [isQueueingPdfExport, setIsQueueingPdfExport] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [autoDownloadArtifactId, setAutoDownloadArtifactId] = useState<string | null>(null);
  const autoDownloadInFlightRef = useRef<string | null>(null);
  // The name typed for a queued export, so the auto-download doesn't wait on the saved-name query.
  const autoDownloadNameRef = useRef<string | null>(null);
  const isDownloadingPdfRef = useRef(false);
  const { data: pdfExport } = useQuery(
    convexQuery(specPilotPdfExportApi.getLatest, enabled ? { submittalId } : "skip")
  );
  const { data: pdfExportDownloadUrl, refetch: refetchPdfExportDownloadUrl } = useQuery(
    convexQuery(specPilotPdfExportApi.getDownloadUrl, enabled ? { submittalId } : "skip")
  );
  const pdfExportStatus = pdfExport?.status ?? "idle";
  const isPdfExportInProgress =
    isQueueingPdfExport ||
    pdfExportStatus === GLOBAL_PROCESSING_PENDING ||
    pdfExportStatus === GLOBAL_PROCESSING_PROCESSING;
  const canExportPdf = hasData && !isPdfExportInProgress;
  const canDownloadPdf =
    !isPdfExportInProgress &&
    !isDownloadingPdf &&
    Boolean(pdfExportDownloadUrl);

  const handleQueuePdfExport = useCallback(async (fileName: string) => {
    if (!enabled || !canExportPdf) return null;
    setIsQueueingPdfExport(true);
    try {
      const result = await generatePdfExport({ submittalId, version });
      autoDownloadNameRef.current = fileName;
      setAutoDownloadArtifactId(String(result.artifactId));
      toast.success("PDF export queued. Download will start when ready.");
      return result.artifactId;
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to queue PDF export");
      console.error("Failed to queue PDF export:", error);
      return null;
    } finally {
      setIsQueueingPdfExport(false);
    }
  }, [enabled, canExportPdf, generatePdfExport, submittalId, version]);

  const handleDownloadPdf = useCallback(async (filename?: string) => {
    if (!enabled || !canDownloadPdf || isDownloadingPdfRef.current) return false;
    isDownloadingPdfRef.current = true;
    setIsDownloadingPdf(true);
    try {
      const { data: refreshedUrl } = await refetchPdfExportDownloadUrl();
      const downloadUrl = refreshedUrl ?? pdfExportDownloadUrl;
      if (!downloadUrl) {
        toast.error("PDF export is not ready yet");
        return false;
      }
      await downloadUrlAsFile(downloadUrl, withExportExtension(filename ?? downloadName, "pdf"));
      recordExportDownloaded(submittalId);
      toast.success("Download started");
      return true;
    } catch (error) {
      notifyError(error, "Failed to download PDF");
      console.error("Failed to download PDF:", error);
      return false;
    } finally {
      isDownloadingPdfRef.current = false;
      setIsDownloadingPdf(false);
    }
  }, [
    enabled,
    canDownloadPdf,
    refetchPdfExportDownloadUrl,
    pdfExportDownloadUrl,
    downloadName,
    recordExportDownloaded,
    submittalId,
  ]);

  useEffect(() => {
    if (!autoDownloadArtifactId) return;
    const currentArtifactId = pdfExport?.artifactId ? String(pdfExport.artifactId) : null;
    if (currentArtifactId !== autoDownloadArtifactId) return;

    if (pdfExportStatus === GLOBAL_PROCESSING_FAILED) {
      toast.error(pdfExport?.error ?? "PDF export failed");
      setAutoDownloadArtifactId(null);
      return;
    }

    if (pdfExportStatus !== GLOBAL_PROCESSING_COMPLETED || !pdfExportDownloadUrl) return;
    if (autoDownloadInFlightRef.current === autoDownloadArtifactId) return;

    autoDownloadInFlightRef.current = autoDownloadArtifactId;
    void (async () => {
      try {
        await handleDownloadPdf(autoDownloadNameRef.current ?? undefined);
      } catch (error) {
        console.error("Failed to auto-download SpecPilot export:", error);
      } finally {
        autoDownloadInFlightRef.current = null;
        setAutoDownloadArtifactId(null);
      }
    })();
  }, [
    autoDownloadArtifactId,
    handleDownloadPdf,
    pdfExport?.artifactId,
    pdfExport?.error,
    pdfExportDownloadUrl,
    pdfExportStatus,
  ]);

  return {
    pdfExport,
    isPdfExportInProgress,
    canExportPdf,
    canDownloadPdf,
    handleQueuePdfExport,
    handleDownloadPdf,
  };
}

const TypicalActionsModalLazy = lazy(async () => {
  const mod = await import("@/components/typical-actions-modal");
  return { default: mod.TypicalActionsModal };
});

const TrackerViewModalLazy = lazy(async () => {
  const mod = await import("@/components/tracker-view-modal");
  return { default: mod.TrackerViewModal };
});

const MergeRowsModalLazy = lazy(async () => {
  const mod = await import("@/components/submittal/merge-rows-modal");
  return { default: mod.MergeRowsModal };
});

function SubmittalPdfControls({
  sourcePdf,
  sourceId,
  initialMode = "modal",
}: {
  sourcePdf: PdfModalPipSourceRef;
  sourceId: string;
  initialMode?: PdfModalPipMode;
}) {
  const [isPdfOpen, setIsPdfOpen] = useState(false);

  return (
    <>
      <Button
        className="fixed bottom-6 right-6 z-50 shadow-lg"
        onClick={() => setIsPdfOpen(true)}
      >
        <FileText className="size-4" />
        View Full PDF
      </Button>
      <PdfModalPip
        open={isPdfOpen}
        onOpenChange={setIsPdfOpen}
        sourcePdf={sourcePdf}
        sourceId={sourceId}
        initialMode={initialMode}
      />
    </>
  );
}

/** Inline header meta: template (verbatim name), row count, and the edited-version marker. */
function SubmittalHeaderMeta({
  rowCount,
  templateName,
  templateVersion,
  activeVersion,
}: {
  rowCount?: number;
  templateName?: string;
  templateVersion?: number;
  activeVersion: VersionType;
}) {
  const templateLabel = `${templateName ?? "Submittal Template"}${templateVersion ? ` v${templateVersion}` : ""}`;
  return (
    <>
      {rowCount !== undefined ? (
        <>
          <Badge variant="outline" className="max-w-[28ch]" title={templateLabel}>
            <span className="truncate">{templateLabel}</span>
          </Badge>
          <span className="whitespace-nowrap text-sm text-muted-foreground">{rowCount} rows</span>
        </>
      ) : null}
      {activeVersion === "edited" && (
        <StatusBadge status="review" label="Edited Version" showSpinner={false} />
      )}
    </>
  );
}

export function SubmittalViewerPage() {
  const jobId = useRouteParam("jobId") ?? "";
  const typedJobId = jobId as Id<"specPilotSubmittals">;
  const { data: job } = useQuery(
    convexQuery(api.specPilot.submittals.get, jobId ? { submittalId: typedJobId } : "skip")
  );

  return (
    <ActionLogProvider
      projectId={job?.projectId ?? null}
      pilot="SpecPilot"
      targetKind="specPilotSubmittal"
      targetId={jobId}
    >
      <SubmittalViewerContent />
    </ActionLogProvider>
  );
}

// eslint-disable-next-line complexity
function SubmittalViewerContent() {
  const canEdit = useCanEdit();
  const jobId = useRouteParam("jobId") ?? "";
  const typedJobId = jobId as Id<"specPilotSubmittals">;

  const {
    job,
    submittal,
    typicals: _typicals,
    trackerItems,
    displayData,
    activeVersion,
    setActiveVersion,
    hasEditedData,
    isLoadingCSV,
    csvError,
    isHydratingEdited,
    editedPayloadError,
    editedData,
    setEditedData,
    setEditedPayloadError,
    generatedData,
  } = useSubmittalData(jobId);

  const recordActionLog = useRecordActionLog();

  const updateEditedData = useMutation(api.specPilot.generatedSubmittals.updateEditedData);
  const clearEditedData = useMutation(api.specPilot.generatedSubmittals.clearEditedData);
  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);
  const recordCsiCorrection = useMutation(api.specPilot.csiCorrections.record);
  const addToTracker = useMutation(api.specPilot.trackerItems.createMany);

  const [isReverting, setIsReverting] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const gridRef = useRef<EditableDataGridHandle>(null);
  const [activeTracker, setActiveTracker] = useState<{ type: TrackerType; customName?: string } | null>(null);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [reviewTimesConfirmed, setReviewTimesConfirmed] = useState(false);
  const [csiCodesVerified, setCsiCodesVerified] = useState(false);
  const [gridHasChanges, setGridHasChanges] = useState(false);
  const [gridIsSaving, setGridIsSaving] = useState(false);
  const [typicalsOpen, setTypicalsOpen] = useState(false);
  const [addRowsOpen, setAddRowsOpen] = useState(false);
  // A member demoted mid-session loses the write flows they had open.
  useOnEditAccessLost(() => {
    setIsMergeModalOpen(false);
    setTypicalsOpen(false);
    setAddRowsOpen(false);
    setSelectedRows(new Set());
  });

  const { data: orgMembers } = useQuery(convexQuery(api.auth.listOrganizationMembersMinimal, {}));
  const { data: sourcePdf } = useQuery(
    convexQuery(
      api.specPilot.submittals.getSourcePdfUrl,
      job ? { submittalId: typedJobId } : "skip"
    )
  );
  const { data: project } = useQuery(
    convexQuery(api.projects.getById, job?.projectId ? { projectId: job.projectId } : "skip")
  );
  const { data: locations } = useQuery(
    convexQuery(
      api.specPilot.customItems.listByProjectAndType,
      job?.projectId ? { projectId: job.projectId, type: "location" } : "skip"
    )
  );
  const { data: contractors } = useQuery(
    convexQuery(
      api.specPilot.customItems.listByProjectAndType,
      job?.projectId ? { projectId: job.projectId, type: "contractor" } : "skip"
    )
  );
  const { data: projectCustomItems } = useQuery(
    convexQuery(
      api.specPilot.customItems.listByProject,
      getProjectCustomItemsArgs(job?.projectId)
    )
  );

  const { data: customTrackerNames } = useQuery(
    convexQuery(
      api.specPilot.trackerItems.listCustomTrackerNames,
      job ? { submittalId: typedJobId } : "skip"
    )
  );
  const createCustomItem = useMutation(api.specPilot.customItems.create);

  const [submittalManager, setSubmittalManager] = useState<string | null>(null);
  const [designReviewTime, setDesignReviewTime] = useState<string>("10");
  const [internalReviewTime, setInternalReviewTime] = useState<string>("0");
  const [configValues, setConfigValues] = useState<Record<string, string>>({});

  const columns = useSubmittalColumnMap(displayData?.headers);
  const templateConfigColumns = useMemo(
    () => getTemplateConfigColumns(submittal?.templateSnapshot, displayData?.headers),
    [submittal?.templateSnapshot, displayData?.headers]
  );

  useEffect(() => {
    setConfigValues(
      Object.fromEntries(
        templateConfigColumns.map((column) => [column.key, column.defaultValue ?? ""])
      )
    );
  }, [templateConfigColumns]);
  const managerSelected = getManagerSelected(templateConfigColumns, configValues, submittalManager);

  const trackerCounts = useMemo(() => {
    const items: TrackerCountItem[] = trackerItems ?? [];
    const counts: Record<"warranty" | "lead_time" | "attic_stock", number> = {
      warranty: 0,
      lead_time: 0,
      attic_stock: 0,
    };
    for (const item of items) {
      if (item.trackerType !== "custom") {
        counts[item.trackerType] += 1;
      }
    }
    return counts;
  }, [trackerItems]);

  const saveEditedSnapshot = useCallback(
    async (headers: string[], rows: string[][]) => {
      if (!submittal) {
        throw new Error("No submittal to save");
      }

      const uploadedRows = await uploadTableRowsPayload({
        generateUploadUrl: async () =>
          await generateR2UploadUrl({
            prefix: "spec-pilot/edited-submittals",
            filename: `${submittal._id}-edited.json.gz`,
          }),
        headers,
        rows,
      });

      await updateEditedData({
        submittalId: submittal._id,
        headers,
        editedFileKey: uploadedRows.rowsStorageKey,
        editedFormat: uploadedRows.rowsFormat,
        editedRowCount: uploadedRows.rowCount,
        editedHeaderCount: headers.length,
        editedVersion: Date.now(),
      });

      setEditedData({ headers, rows });
      setEditedPayloadError(null);
    },
    [submittal, generateR2UploadUrl, updateEditedData, setEditedData, setEditedPayloadError]
  );

  const handleSave = useCallback(
    async (headers: string[], rows: string[][]) => {
      if (!submittal) {
        throw new Error("No submittal to save");
      }

      const csiCol = findExactColumnIndex(headers, [
        "CSI Code",
        "Submittal Spec Section Number",
      ]);
      const csiTitleIdx = findExactColumnIndex(headers, [
        "CSI Title",
        "Submittal Spec Section Description",
      ]);
      const sourceTableIdx = findExactColumnIndex(headers, ["Source Table"]);
      const titleIdx = findExactColumnIndex(headers, ["Title", "Submittal Title"]);
      const descIdx = findExactColumnIndex(headers, ["Description"]);

      if (csiCol >= 0 && displayData) {
        for (let i = 0; i < rows.length; i++) {
          const newCode = rows[i]?.[csiCol] ?? "";
          const oldCode = displayData.rows[i]?.[csiCol] ?? "";
          if (newCode && oldCode && newCode !== oldCode) {
            recordCsiCorrection({
              submittalId: typedJobId,
              title: titleIdx >= 0 ? (rows[i][titleIdx] ?? "") : "",
              itemFields: [
                titleIdx >= 0 ? rows[i][titleIdx] : "",
                descIdx >= 0 ? rows[i][descIdx] : "",
              ]
                .filter(Boolean)
                .join(" | "),
              originalCode: oldCode,
              correctedCode: newCode,
              correctedTitle: csiTitleIdx >= 0 ? (rows[i][csiTitleIdx] ?? "") : "",
              tableContext: sourceTableIdx >= 0 ? (rows[i][sourceTableIdx] ?? "") : "",
            }).catch((err) => {
              console.error("Failed to record CSI correction:", err);
            });
            setCsiCodesVerified(false);
          }
        }
      }

      await saveEditedSnapshot(headers, rows);
      await recordActionLog();
      setActiveVersion("edited");
      toast.success("Changes saved successfully");
    },
    [submittal, saveEditedSnapshot, recordCsiCorrection, displayData, typedJobId, setActiveVersion, recordActionLog]
  );

  const handleRevert = useCallback(async () => {
    if (!submittal) return;

    setIsReverting(true);
    try {
      await clearEditedData({ submittalId: submittal._id });
      await recordActionLog();
      setActiveVersion("generated");
      setEditedData(null);
      setEditedPayloadError(null);
      toast.success("Reverted to generated version");
    } catch (error) {
      notifyError(error, "Failed to revert changes");
      console.error("Error reverting:", error);
    } finally {
      setIsReverting(false);
    }
  }, [submittal, clearEditedData, setActiveVersion, setEditedData, setEditedPayloadError, recordActionLog]);

  const recordExportDownloaded = useRecordExportDownloaded();
  const exportFilenames = useExportFilenames({ kind: "specPilotSubmittal", id: typedJobId });
  const submittalLogTarget = exportTarget("spec_pilot_submittal_csv", {}, { projectName: project?.name });
  const submittalLogName = exportFilenames.resolve(submittalLogTarget.key, submittalLogTarget.defaultName);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const handleDownloadCsv = useCallback(
    (fileName: string) => {
      if (!job || !displayData) return false;
      const csvContent = toCSV(displayData.headers, displayData.rows);
      downloadFile(csvContent, withExportExtension(fileName, "csv"), "text/csv");
      recordExportDownloaded(job._id);
      toast.success("Download started");
      return true;
    },
    [job, displayData, recordExportDownloaded]
  );

  const {
    pdfExport,
    isPdfExportInProgress,
    canExportPdf,
    canDownloadPdf,
    handleQueuePdfExport,
    handleDownloadPdf,
  } = useSpecPilotPdfExport({
    enabled: Boolean(job),
    submittalId: typedJobId,
    version: activeVersion,
    hasData: Boolean(displayData),
    downloadName: submittalLogName,
  });

  const {
    handleBulkCSIAssign,
    handleBulkColumnAssign,
    handleConfigColumnAssign,
    handleSubmittalManagerChange,
  } = useSubmittalBulkActions({
    gridRef,
    selectedRows,
    setSelectedRows,
    csiCodeCol: columns.csiCode,
    csiTitleCol: columns.csiTitle,
    submittalManagerCol: columns.submittalManager,
    displayRows: displayData?.rows,
    packageSpecSectionNumberCol: columns.packageSpecSectionNumber,
    packageSpecSectionDescriptionCol: columns.packageSpecSectionDescription,
    submittalNumberCol: columns.submittalNumber,
  });

  const handleTemplateConfigChange = useCallback(
    (column: TemplateConfigColumn, value: string) => {
      setConfigValues((current) => ({ ...current, [column.key]: value }));
      setReviewTimesConfirmed(false);
      handleConfigColumnAssign(column.columnIndex, value, column.header);
    },
    [handleConfigColumnAssign]
  );

  const cellRenderer = useSubmittalCellRenderer({
    columns,
    gridRef,
    displayRows: displayData?.rows,
    projectId: job?.projectId,
    locations: locations ?? [],
    contractors: contractors ?? [],
    orgMembers: orgMembers ?? [],
    projectCustomItems: projectCustomItems ?? [],
    templateConfigColumns,
    createCustomItem,
  });

  const handleAddToTracker = useCallback(
    async (trackerType: TrackerType, customTrackerName?: string) => {
      if (selectedRows.size === 0 || !displayData) {
        toast.error("No rows selected");
        return;
      }

      const items = Array.from(selectedRows)
        .filter((rowIdx) => displayData.rows[rowIdx] !== undefined)
        .map((rowIdx) => {
          const data: Record<string, string> = {};
          for (let i = 0; i < displayData.headers.length; i++) {
            data[displayData.headers[i]] = displayData.rows[rowIdx]?.[i] ?? "";
          }
          return data;
        });

      try {
        const result = await addToTracker({
          submittalId: typedJobId,
          trackerType,
          items,
          customTrackerName: trackerType === "custom" ? customTrackerName : undefined,
        });
        if (result && result.skipped > 0) {
          toast.info(`Added ${result.added} item(s), ${result.skipped} duplicate(s) skipped`);
        } else {
          toast.success(`Added ${result?.added ?? items.length} item(s) to tracker`);
        }
        await recordActionLog();
        setSelectedRows(new Set());
      } catch (error) {
        notifyError(error, "Failed to add to tracker");
        console.error("Error adding to tracker:", error);
      }
    },
    [selectedRows, displayData, addToTracker, typedJobId, recordActionLog]
  );

  const handleDeleteRows = useCallback(async () => {
    if (!submittal || !displayData || selectedRows.size === 0) return;

    const filteredRows = displayData.rows.filter((_, index) => !selectedRows.has(index));
    const count = displayData.rows.length - filteredRows.length;

    if (count === 0) {
      setSelectedRows(new Set());
      return;
    }

    await saveEditedSnapshot(displayData.headers, filteredRows);
    await recordActionLog();
    setSelectedRows(new Set());
    setActiveVersion("edited");
    toast.success(`Deleted ${count} row${count !== 1 ? "s" : ""}`);
  }, [submittal, displayData, selectedRows, saveEditedSnapshot, setActiveVersion, recordActionLog]);

  const handleMergeRows = useCallback(
    async (mergedRow: string[]) => {
      if (!submittal || !displayData || selectedRows.size < 2) return;

      const sortedSelected = Array.from(selectedRows).sort((a, b) => a - b);
      const firstSelectedIdx = sortedSelected[0];

      const updatedRows = displayData.rows.filter((_, index) => !selectedRows.has(index));
      updatedRows.splice(firstSelectedIdx, 0, mergedRow);

      try {
        await saveEditedSnapshot(displayData.headers, updatedRows);
        await recordActionLog();
        setSelectedRows(new Set());
        setActiveVersion("edited");
        toast.success(`Merged ${selectedRows.size} rows into one`);
      } catch (error) {
        console.error("Failed to merge rows:", error);
        notifyError(error, "Failed to merge rows. Please try again.");
      }
    },
    [submittal, displayData, selectedRows, saveEditedSnapshot, setActiveVersion, recordActionLog]
  );

  const pageHeaderMeta = useMemo(
    () => (
      <SubmittalHeaderMeta
        rowCount={displayData?.rows.length}
        templateName={submittal?.templateName}
        templateVersion={submittal?.templateVersion}
        activeVersion={activeVersion}
      />
    ),
    [displayData, submittal?.templateName, submittal?.templateVersion, activeVersion]
  );

  const pageHeaderActions = useMemo(
    () => (
      <ExportButton onClick={() => setIsExportOpen(true)} busy={isPdfExportInProgress} />
    ),
    [isPdfExportInProgress]
  );

  const handleAddRows = useCallback((rowCount: number) => {
    if (!gridRef.current) {
      toast.error("Grid is not ready yet");
      return;
    }
    gridRef.current.addRows(rowCount);
    toast.success(`Added ${rowCount} blank row${rowCount === 1 ? "" : "s"}`);
  }, []);

  const handleCreateCustomTracker = useCallback(
    async (name: string) => {
      if (selectedRows.size > 0) {
        await handleAddToTracker("custom", name);
        toast.success(`Custom tracker "${name}" created with ${selectedRows.size} item(s)!`);
      } else {
        toast.success(`Custom tracker "${name}" created! Select rows and use "Add to Tracker" to add items.`);
      }
    },
    [selectedRows, handleAddToTracker]
  );

  const selectionToolbar =
    canEdit && selectedRows.size > 0 ? (
      <SubmittalSelectionToolbar
        className="min-w-0 flex-1"
        selectedRowCount={selectedRows.size}
        headers={getDisplayHeaders(displayData)}
        csiCodeCol={columns.csiCode}
        submittalStatusCol={columns.submittalStatus}
        submittalTypeCol={columns.submittalType}
        trackerCounts={trackerCounts}
        customTrackerNames={customTrackerNames ?? []}
        onBulkCSIAssign={(...args) => {
          setCsiCodesVerified(false);
          return handleBulkCSIAssign(...args);
        }}
        onBulkColumnAssign={handleBulkColumnAssign}
        onAddToTracker={handleAddToTracker}
        onCreateCustomTracker={handleCreateCustomTracker}
        onDeleteRows={handleDeleteRows}
        onMergeRows={() => setIsMergeModalOpen(true)}
        onClearSelection={() => setSelectedRows(new Set())}
      />
    ) : null;

  const gridToolbar = useMemo(
    () => (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {selectionToolbar ?? (
          <>
            {hasEditedData ? (
              <Tabs value={activeVersion} onValueChange={(v) => setActiveVersion(v as VersionType)}>
                <TabsList>
                  <TabsTrigger value="generated">Generated</TabsTrigger>
                  <TabsTrigger value="edited">Edited</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
            {canEdit && displayData ? (
              <span className="hidden text-xs text-muted-foreground md:inline">
                Select rows for bulk actions
              </span>
            ) : null}
          </>
        )}

        {canEdit && (
          <div className="ms-auto flex flex-wrap items-center gap-2">
            {gridHasChanges && (
              <>
                <Button variant="outline" size="sm" onClick={() => gridRef.current?.cancel()} disabled={gridIsSaving}>
                  <X />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      await gridRef.current?.save();
                    } catch (error) {
                      console.error("Failed to save grid changes:", error);
                      notifyError(error, "Failed to save changes");
                    }
                  }}
                  disabled={gridIsSaving}
                >
                  {gridIsSaving ? <Loader2 className="animate-spin" /> : <Save />}
                  {gridIsSaving ? "Saving..." : "Save Changes"}
                </Button>
              </>
            )}
            {hasEditedData ? (
              <DeleteConfirmationDialog
                title="Revert to generated version?"
                description="This will discard all your edits and restore the original generated submittal. This action cannot be undone."
                onConfirm={handleRevert}
                disabled={isReverting}
                confirmLabel="Revert"
              >
                <Button variant="outline" size="sm" disabled={isReverting}>
                  {isReverting ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                  Revert to Generated
                </Button>
              </DeleteConfirmationDialog>
            ) : null}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton
                  label="Add submittal rows"
                  icon={Plus}
                  variant="outline"
                  disabled={gridIsSaving}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setTypicalsOpen(true)}>
                  Typicals
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setAddRowsOpen(true)}>
                  Blank Rows…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Suspense fallback={null}>
              <TypicalActionsModalLazy
                submittalId={typedJobId}
                open={typicalsOpen}
                onOpenChange={setTypicalsOpen}
                trigger={null}
              />
            </Suspense>
            <AddRowsDialog open={addRowsOpen} onOpenChange={setAddRowsOpen} onAddRows={handleAddRows} />
          </div>
        )}
      </div>
    ),
    [
      selectionToolbar,
      canEdit,
      displayData,
      hasEditedData,
      activeVersion,
      setActiveVersion,
      gridHasChanges,
      gridIsSaving,
      isReverting,
      handleRevert,
      typedJobId,
      typicalsOpen,
      addRowsOpen,
      handleAddRows,
    ]
  );

  const trackerStrip = (
    <SubmittalTrackerStrip
      trackerCounts={trackerCounts}
      customTrackerNames={customTrackerNames ?? []}
      onTrackerClick={(type, customName) => setActiveTracker({ type, customName })}
    />
  );

  const stateGate = getSubmittalStateGate({
    jobId,
    job,
    submittal,
    isLoadingCSV,
    generatedData,
    editedData,
    csvError,
    isHydratingEdited,
    editedPayloadError,
    activeVersion,
    hasEditedData,
  });

  if (stateGate) {
    return stateGate;
  }

  if (!job || !submittal) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <ProcessingSpinner message="Loading submittal data..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Submittal"
        trail={[
          { label: "SpecPilot", href: "/specPilot" },
          { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
        ]}
        meta={pageHeaderMeta}
        actions={pageHeaderActions}
      />

      {canEdit ? (
        <SubmittalSetupPanel
          orgMembers={orgMembers ?? []}
          templateConfigColumns={templateConfigColumns}
          configValues={configValues}
          selectedRowCount={selectedRows.size}
          submittalManager={submittalManager}
          managerSelected={managerSelected}
          designReviewTime={designReviewTime}
          internalReviewTime={internalReviewTime}
          projectCustomItems={projectCustomItems ?? []}
          projectId={job.projectId}
          csiCodeCol={columns.csiCode}
          isProcoreTemplate={submittal.templateName === "Procore Import"}
          reviewTimesConfirmed={reviewTimesConfirmed}
          csiCodesVerified={csiCodesVerified}
          createCustomItem={createCustomItem}
          onSubmittalManagerChange={(managerEmail) => {
            setSubmittalManager(managerEmail);
            handleSubmittalManagerChange(managerEmail);
          }}
          onDesignReviewTimeChange={(value) => {
            setDesignReviewTime(value);
            setReviewTimesConfirmed(false);
            if (selectedRows.size > 0) {
              handleBulkColumnAssign(columns.designReviewTime, value, "Design Team Review Time");
            }
          }}
          onInternalReviewTimeChange={(value) => {
            setInternalReviewTime(value);
            setReviewTimesConfirmed(false);
            if (selectedRows.size > 0) {
              handleBulkColumnAssign(columns.internalReviewTime, value, "Internal Review Time");
            }
          }}
          onConfigColumnChange={handleTemplateConfigChange}
          onConfirmReviewTimes={() => setReviewTimesConfirmed(true)}
          onVerifyCSICodes={() => setCsiCodesVerified(true)}
          trailing={trackerStrip}
        />
      ) : (
        <div className="flex min-h-10 items-center rounded-sm border px-1.5 py-1">{trackerStrip}</div>
      )}

      {gridToolbar}

      {editedPayloadError && activeVersion === "edited" ? (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Failed to load edited data</AlertTitle>
          <AlertDescription>{editedPayloadError}</AlertDescription>
        </Alert>
      ) : null}

      {displayData ? (
        <ProcoreTemplateGrid
          ref={gridRef}
          key={`${activeVersion}-${submittal._id}`}
          headers={displayData.headers}
          rows={displayData.rows}
          onSave={handleSave}
          readOnly={!canEdit}
          selectable={canEdit}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          cellRenderer={cellRenderer}
          packageColumnIndex={columns.packageTitle}
          templateLabel={getTemplateLabel(submittal.templateName)}
          showHeader={false}
          hideActionBar
          onHasChangesChange={setGridHasChanges}
          onIsSavingChange={setGridIsSaving}
        />
      ) : (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          No data available
        </div>
      )}

      {activeTracker !== null ? (
        <Suspense fallback={null}>
          <TrackerViewModalLazy
            submittalId={typedJobId}
            trackerType={activeTracker.type}
            customTrackerName={activeTracker.customName}
            isOpen
            onClose={() => setActiveTracker(null)}
            projectName={project?.name}
          />
        </Suspense>
      ) : null}

      <SubmittalExportDialog
        open={isExportOpen}
        onOpenChange={setIsExportOpen}
        version={activeVersion}
        defaultFileName={submittalLogName}
        pdf={{
          latest: pdfExport,
          canGenerate: canExportPdf,
          canDownload: canDownloadPdf,
          queue: handleQueuePdfExport,
          download: handleDownloadPdf,
        }}
        downloadCsv={handleDownloadCsv}
        canDownloadCsv={Boolean(displayData)}
        exportFilenames={exportFilenames}
      />

      {selectedRows.size >= 2 && displayData ? (
        <Suspense fallback={null}>
          <MergeRowsModalLazy
            open={isMergeModalOpen}
            onOpenChange={setIsMergeModalOpen}
            headers={displayData.headers}
            selectedRows={Array.from(selectedRows)
              .sort((a, b) => a - b)
              .map((idx) => displayData.rows[idx])
              .filter((row): row is string[] => row !== undefined)}
            csiCodeCol={columns.csiCode}
            csiTitleCol={columns.csiTitle}
            packageSpecSectionNumberCol={columns.packageSpecSectionNumber}
            packageSpecSectionDescriptionCol={columns.packageSpecSectionDescription}
            locationCol={columns.location}
            submittalNumberCol={columns.submittalNumber}
            locations={locations ?? []}
            projectId={job?.projectId}
            createCustomItem={createCustomItem}
            onMerge={handleMergeRows}
          />
        </Suspense>
      ) : null}

      {sourcePdf?.fileUrl ? (
        <SubmittalPdfControls sourcePdf={sourcePdf} sourceId={typedJobId} />
      ) : null}
    </div>
  );
}
